import { Injectable, OnDestroy, signal, computed } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import * as THREE from 'three';

import {
    CollabUser,
    CollabCursorData,
    CollabAvatarData,
    CollabSelectionData,
    CollabCameraData,
    CollabMarkupData,
    RoomJoinedData,
    RoomUserJoinedData,
    RoomUserLeftData,
    MarkupSyncData,
    SceneObjectTransformData,
    SceneObjectVisibilityData,
    SceneObjectColorData,
    SceneObjectDeleteData,
    SceneObjectSyncData,
    FileShareReadyData,
    COLLAB_EVENTS,
    COLLAB_CONFIG,
    toVec3Tuple,
    generateMarkupId,
    MarkupType,
} from '../IFikoThreeJS/fiko-collab/fiko-collab-types';

import { FikoCollabCursor } from '../IFikoThreeJS/fiko-collab/FikoCollabCursor';
import { FikoCollabAvatar } from '../IFikoThreeJS/fiko-collab/FikoCollabAvatar';
import { FikoCollabHighlightManager } from '../IFikoThreeJS/fiko-collab/FikoCollabHighlight';
import { FikoCollabMarkupManager } from '../IFikoThreeJS/fiko-collab/FikoCollabMarkupManager';

/**
 * FikoYT CAD Real-Time Collaboration Service
 *
 * Provides multi-user collaboration features:
 * - Live cursor presence
 * - User avatars in 3D scene
 * - Selection broadcasting
 * - Camera follow mode
 * - Real-time markup annotations
 */
@Injectable({ providedIn: 'root' })
export class FikoCollabService implements OnDestroy {
    // ========================================================================
    // State Signals
    // ========================================================================

    private _isConnected = signal(false);
    private _currentRoomId = signal<string | null>(null);
    private _currentUserId = signal<string | null>(null);
    private _connectedUsers = signal<CollabUser[]>([]);
    private _followingUserId = signal<string | null>(null);

    // Remote user camera data for UI display
    private _remoteUserCameras = signal<Map<string, {
        position: [number, number, number];
        target: [number, number, number];
        lastUpdate: number;
    }>>(new Map());

    readonly isConnected = this._isConnected.asReadonly();
    readonly currentRoomId = this._currentRoomId.asReadonly();
    readonly currentUserId = this._currentUserId.asReadonly();
    readonly connectedUsers = this._connectedUsers.asReadonly();
    readonly followingUserId = this._followingUserId.asReadonly();
    readonly isFollowing = computed(() => this._followingUserId() !== null);
    readonly remoteUserCameras = this._remoteUserCameras.asReadonly();

    // ========================================================================
    // Private State
    // ========================================================================

    private socket: Socket | null = null;
    private scene: THREE.Scene | null = null;
    private camera: THREE.Camera | null = null;
    private orbitControls: any = null;  // OrbitControls reference for target & zoom
    private collabContainer: THREE.Group | null = null;

    // Managers
    private cursorManager: Map<string, FikoCollabCursor> = new Map();
    private avatarManager: Map<string, FikoCollabAvatar> = new Map();
    private highlightManager: FikoCollabHighlightManager | null = null;
    private markupManager: FikoCollabMarkupManager | null = null;

    // User info
    private userName: string = '';
    private userColor: string = '';
    private projectId: string = '';

    // Throttle timers
    private cursorThrottleTimer: number | null = null;
    private avatarThrottleTimer: number | null = null;
    private cameraThrottleTimer: number | null = null;
    private lastCursorEmit = 0;
    private lastAvatarEmit = 0;
    private lastCameraEmit = 0;

    // Cursor hide timer
    private cursorHideTimers: Map<string, number> = new Map();

    // Reconnect tracking
    private _hasConnectedOnce = false;

    // Room event callbacks (set by external components)
    public onRoomJoinedCallback: (() => void) | null = null;
    public onRoomLeftCallback: (() => void) | null = null;

    // ========================================================================
    // Initialization
    // ========================================================================

    constructor() {}

    /**
     * Initialize the collaboration service
     */
    initialize(scene: THREE.Scene, camera: THREE.Camera, serverUrl: string, orbitControls?: any): void {
        console.log('[FikoCollab] Initializing with scene:', !!scene, 'camera:', !!camera);
        this.scene = scene;
        this.camera = camera;
        this.orbitControls = orbitControls || null;

        // Create container for collaboration objects
        this.collabContainer = new THREE.Group();
        this.collabContainer.name = 'CollabContainer';
        this.collabContainer.renderOrder = 9999; // Render on top
        this.scene.add(this.collabContainer);
        console.log('[FikoCollab] CollabContainer added to scene');

        // Initialize managers
        this.highlightManager = new FikoCollabHighlightManager(scene);
        this.markupManager = new FikoCollabMarkupManager(this.collabContainer);

        // Connect to server
        this.connect(serverUrl);
    }

    /**
     * Set orbit controls reference (can be called after initialize)
     */
    setOrbitControls(controls: any): void {
        this.orbitControls = controls;
    }

    /**
     * Connect to the collaboration server
     */
    private connect(serverUrl: string): void {
        if (this.socket?.connected) {
            return;
        }

        this.socket = io(serverUrl, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
        });

        this.setupSocketListeners();
        this.setupSceneSyncListeners();
    }

    /**
     * Setup socket event listeners
     */
    private setupSocketListeners(): void {
        if (!this.socket) return;

        this.socket.on('connect', () => {
            this._isConnected.set(true);

            if (this._hasConnectedOnce) {
                console.log('[FikoCollab] Reconnected to server, re-syncing state...');
                this.resyncAfterReconnect();
            } else {
                this._hasConnectedOnce = true;
                console.log('[FikoCollab] Connected to server');
            }
        });

        this.socket.on('disconnect', (reason: string) => {
            this._isConnected.set(false);
            console.error('[FikoCollab] Disconnected from server, reason:', reason);
        });

        // Room events
        this.socket.on(COLLAB_EVENTS.ROOM_JOINED, (data: RoomJoinedData) => {
            console.log('[FikoCollab] Joined room:', data.roomId);
            const users = data.users ?? [];
            this._connectedUsers.set(users);

            // Initialize existing users
            for (const user of users) {
                this.createUserCursor(user);
                this.createUserAvatar(user);
            }

            // Initialize existing markups
            if (data.markups) {
                for (const markup of data.markups) {
                    this.markupManager?.addMarkup(markup);
                }
            }

            // Trigger callback for external components (e.g., to enable selection sync)
            console.log('[FikoCollab] Triggering onRoomJoinedCallback');
            this.onRoomJoinedCallback?.();
        });

        this.socket.on(COLLAB_EVENTS.ROOM_USER_JOINED, (data: RoomUserJoinedData) => {
            console.log('[FikoCollab] User joined:', data.user.userName);
            this._connectedUsers.update(users => [...users, data.user]);
            this.createUserCursor(data.user);
            this.createUserAvatar(data.user);
        });

        this.socket.on(COLLAB_EVENTS.ROOM_USER_LEFT, (data: RoomUserLeftData) => {
            console.log('[FikoCollab] User left:', data.userId);
            this._connectedUsers.update(users => users.filter(u => u.userId !== data.userId));
            this.removeUserCursor(data.userId);
            this.removeUserAvatar(data.userId);
            this.highlightManager?.clearUserHighlights(data.userId);

            // Remove camera data for this user
            const cameras = new Map(this._remoteUserCameras());
            cameras.delete(data.userId);
            this._remoteUserCameras.set(cameras);

            // Stop following if the followed user left
            if (this._followingUserId() === data.userId) {
                this.stopFollowing();
            }
        });

        // Cursor events
        this.socket.on(COLLAB_EVENTS.CURSOR_UPDATE, (data: CollabCursorData) => {
            this.handleCursorUpdate(data);
        });

        // Avatar events
        this.socket.on(COLLAB_EVENTS.AVATAR_SYNC, (data: CollabAvatarData) => {
            this.handleAvatarSync(data);
        });

        // Selection events
        this.socket.on(COLLAB_EVENTS.SELECTION_BROADCAST, (data: CollabSelectionData) => {
            this.handleSelectionBroadcast(data);
        });

        // Camera events
        this.socket.on(COLLAB_EVENTS.CAMERA_BROADCAST, (data: CollabCameraData) => {
            this.handleCameraBroadcast(data);
        });

        this.socket.on(COLLAB_EVENTS.CAMERA_FOLLOW_STARTED, () => {
            console.log('[FikoCollab] Following started');
        });

        this.socket.on(COLLAB_EVENTS.CAMERA_FOLLOW_STOPPED, () => {
            console.log('[FikoCollab] Following stopped');
            this._followingUserId.set(null);
        });

        // Markup events
        this.socket.on(COLLAB_EVENTS.MARKUP_SYNC, (data: MarkupSyncData) => {
            this.handleMarkupSync(data);
        });
    }

    // ========================================================================
    // Reconnect State Sync
    // ========================================================================

    private resyncAfterReconnect(): void {
        const roomId = this._currentRoomId();
        const userId = this._currentUserId();
        if (!roomId || !userId || !this.socket) return;

        this.clearAllUsers();

        this.socket.emit(COLLAB_EVENTS.ROOM_JOIN, {
            roomId,
            projectId: this.projectId,
            userId,
            userName: this.userName,
            userColor: this.userColor,
        });
        console.log('[FikoCollab] Re-joined room after reconnect:', roomId);
    }

    // ========================================================================
    // Room Management
    // ========================================================================

    /**
     * Join a collaboration room
     */
    joinRoom(roomId: string, projectId: string, userId: string, userName: string, userColor?: string): void {
        if (!this.socket || !this._isConnected()) {
            console.warn('[FikoCollab] Cannot join room - not connected');
            return;
        }

        this._currentRoomId.set(roomId);
        this._currentUserId.set(userId);
        this.userName = userName;
        this.userColor = userColor || '';
        this.projectId = projectId;

        this.socket.emit(COLLAB_EVENTS.ROOM_JOIN, {
            roomId,
            projectId,
            userId,
            userName,
            userColor,
        });
    }

    /**
     * Leave the current room
     */
    leaveRoom(): void {
        if (!this.socket || !this._currentRoomId()) {
            return;
        }

        this.socket.emit(COLLAB_EVENTS.ROOM_LEAVE, {
            roomId: this._currentRoomId(),
            userId: this._currentUserId(),
        });

        // Cleanup
        this.clearAllUsers();
        this._currentRoomId.set(null);
        this._connectedUsers.set([]);

        // Trigger callback for external components
        this.onRoomLeftCallback?.();
    }

    // ========================================================================
    // Cursor Management
    // ========================================================================

    /**
     * Emit cursor position (throttled)
     */
    emitCursorMove(ray: THREE.Ray, intersection?: THREE.Intersection): void {
        const now = Date.now();
        if (now - this.lastCursorEmit < COLLAB_CONFIG.CURSOR_THROTTLE) {
            return;
        }
        this.lastCursorEmit = now;

        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) {
            return;
        }

        const data = {
            roomId: this._currentRoomId(),
            userId: this._currentUserId(),
            userName: this.userName,
            userColor: this.userColor,
            rayOrigin: toVec3Tuple(ray.origin.x, ray.origin.y, ray.origin.z),
            rayDirection: toVec3Tuple(ray.direction.x, ray.direction.y, ray.direction.z),
            worldPosition: intersection ? toVec3Tuple(
                intersection.point.x,
                intersection.point.y,
                intersection.point.z
            ) : undefined,
            hoveredUuid: intersection?.object?.uuid,
        };

        console.log('[FikoCollab] Emitting cursor move', data.rayOrigin);
        this.socket.emit(COLLAB_EVENTS.CURSOR_MOVE, data);
    }

    /**
     * Hide cursor (when not moving)
     */
    hideCursor(): void {
        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) {
            return;
        }

        this.socket.emit(COLLAB_EVENTS.CURSOR_HIDE, {
            roomId: this._currentRoomId(),
            userId: this._currentUserId(),
        });
    }

    private handleCursorUpdate(data: CollabCursorData): void {
        console.log('[FikoCollab] Received cursor update from:', data.userId, 'known cursors:', Array.from(this.cursorManager.keys()));
        if (data.userId === this._currentUserId()) return;

        let cursor = this.cursorManager.get(data.userId);
        if (!cursor) {
            console.warn('[FikoCollab] No cursor found for user:', data.userId, '- creating now');
            // Try to create cursor on the fly if we have user info
            this.createUserCursor({
                userId: data.userId,
                userName: data.userName || 'Unknown',
                userColor: data.userColor || '#00FFFF',
                socketId: '',
                isActive: true
            });
            cursor = this.cursorManager.get(data.userId);
            if (!cursor) {
                console.error('[FikoCollab] Failed to create cursor for:', data.userId);
                return;
            }
        }

        if (data.hidden) {
            cursor.visible = false;
            return;
        }

        cursor.visible = true;
        cursor.updateFromData(data);
        console.log('[FikoCollab] Cursor updated - visible:', cursor.visible, 'position:', data.rayOrigin);

        // Reset hide timer
        const existingTimer = this.cursorHideTimers.get(data.userId);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }
        this.cursorHideTimers.set(data.userId, window.setTimeout(() => {
            const c = this.cursorManager.get(data.userId);
            if (c) c.visible = false;
        }, COLLAB_CONFIG.CURSOR_HIDE_DELAY));
    }

    private createUserCursor(user: CollabUser): void {
        console.log('[FikoCollab] Creating cursor for user:', user.userId, user.userName);
        if (this.cursorManager.has(user.userId)) {
            console.log('[FikoCollab] Cursor already exists for:', user.userId);
            return;
        }

        const cursor = new FikoCollabCursor(user.userId, user.userName, user.userColor);
        cursor.visible = false;
        this.collabContainer?.add(cursor);
        this.cursorManager.set(user.userId, cursor);
        console.log('[FikoCollab] Cursor created and added to scene. Container:', !!this.collabContainer);
    }

    private removeUserCursor(userId: string): void {
        const cursor = this.cursorManager.get(userId);
        if (cursor) {
            this.collabContainer?.remove(cursor);
            cursor.dispose();
            this.cursorManager.delete(userId);
        }

        const timer = this.cursorHideTimers.get(userId);
        if (timer) {
            clearTimeout(timer);
            this.cursorHideTimers.delete(userId);
        }
    }

    // ========================================================================
    // Avatar Management
    // ========================================================================

    /**
     * Emit camera position for avatar (throttled)
     */
    emitAvatarUpdate(): void {
        const now = Date.now();
        if (now - this.lastAvatarEmit < COLLAB_CONFIG.AVATAR_THROTTLE) {
            return;
        }
        this.lastAvatarEmit = now;

        if (!this.socket || !this._currentRoomId() || !this._currentUserId() || !this.camera) {
            return;
        }

        const position = this.camera.position;
        const target = new THREE.Vector3();
        this.camera.getWorldDirection(target).multiplyScalar(10).add(position);

        const data = {
            roomId: this._currentRoomId(),
            userId: this._currentUserId(),
            userName: this.userName,
            userColor: this.userColor,
            cameraPosition: toVec3Tuple(position.x, position.y, position.z),
            cameraTarget: toVec3Tuple(target.x, target.y, target.z),
            cameraUp: toVec3Tuple(this.camera.up.x, this.camera.up.y, this.camera.up.z),
            isActive: true,
        };

        this.socket.emit(COLLAB_EVENTS.AVATAR_UPDATE, data);
    }

    private handleAvatarSync(data: CollabAvatarData): void {
        if (data.userId === this._currentUserId()) return;

        const avatar = this.avatarManager.get(data.userId);
        if (!avatar) return;

        avatar.updateFromData(data);

        // Store camera data for UI display
        const cameras = new Map(this._remoteUserCameras());
        cameras.set(data.userId, {
            position: data.cameraPosition,
            target: data.cameraTarget,
            lastUpdate: Date.now(),
        });
        this._remoteUserCameras.set(cameras);
    }

    private createUserAvatar(user: CollabUser): void {
        if (this.avatarManager.has(user.userId)) return;

        const avatar = new FikoCollabAvatar(user.userId, user.userName, user.userColor);
        this.collabContainer?.add(avatar);
        this.avatarManager.set(user.userId, avatar);
    }

    private removeUserAvatar(userId: string): void {
        const avatar = this.avatarManager.get(userId);
        if (avatar) {
            this.collabContainer?.remove(avatar);
            avatar.dispose();
            this.avatarManager.delete(userId);
        }
    }

    // ========================================================================
    // Selection Broadcasting
    // ========================================================================

    /**
     * Emit selection change
     */
    emitSelectionChanged(selectedUuids: string[], mode: 'replace' | 'add' | 'remove' = 'replace'): void {
        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) {
            return;
        }

        const data = {
            roomId: this._currentRoomId(),
            userId: this._currentUserId(),
            userName: this.userName,
            userColor: this.userColor,
            selectedUuids,
            selectionMode: mode,
        };

        console.log('[FikoCollab] Emitting selection change:', selectedUuids.length, 'items');
        this.socket.emit(COLLAB_EVENTS.SELECTION_CHANGED, data);
    }

    /**
     * Clear selection
     */
    emitSelectionClear(): void {
        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) {
            return;
        }

        this.socket.emit(COLLAB_EVENTS.SELECTION_CLEAR, {
            roomId: this._currentRoomId(),
            userId: this._currentUserId(),
        });
    }

    private handleSelectionBroadcast(data: CollabSelectionData): void {
        console.log('[FikoCollab] Received selection broadcast from:', data.userId, 'items:', data.selectedUuids?.length);
        if (data.userId === this._currentUserId()) return;

        if (!data.userColor) return;

        this.highlightManager?.applyRemoteSelection(
            data.userId,
            data.selectedUuids,
            data.userColor
        );
    }

    // ========================================================================
    // Camera Follow
    // ========================================================================

    /**
     * Start following a user's camera
     */
    startFollowing(targetUserId: string): void {
        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) {
            return;
        }

        this._followingUserId.set(targetUserId);

        this.socket.emit(COLLAB_EVENTS.CAMERA_FOLLOW_START, {
            roomId: this._currentRoomId(),
            followerId: this._currentUserId(),
            targetUserId,
        });
    }

    /**
     * Stop following
     */
    stopFollowing(): void {
        if (!this.socket || !this._currentUserId()) {
            return;
        }

        this._followingUserId.set(null);

        this.socket.emit(COLLAB_EVENTS.CAMERA_FOLLOW_STOP, {
            followerId: this._currentUserId(),
        });
    }

    /**
     * Emit camera update for followers (throttled)
     */
    emitCameraUpdate(): void {
        const now = Date.now();
        if (now - this.lastCameraEmit < COLLAB_CONFIG.CAMERA_THROTTLE) {
            return;
        }
        this.lastCameraEmit = now;

        if (!this.socket || !this._currentRoomId() || !this._currentUserId() || !this.camera) {
            return;
        }

        const position = this.camera.position;

        // Use OrbitControls target if available (actual look-at point)
        // Otherwise calculate from camera direction
        let target: THREE.Vector3;
        if (this.orbitControls?.target) {
            target = this.orbitControls.target;
        } else {
            target = new THREE.Vector3();
            this.camera.getWorldDirection(target).multiplyScalar(10).add(position);
        }

        // Calculate effective zoom: for PerspectiveCamera use distance-to-target as zoom proxy
        // For OrthographicCamera use camera.zoom directly
        let zoom = 1;
        if ((this.camera as any).isPerspectiveCamera) {
            // Distance from camera to orbit target = effective "zoom"
            zoom = position.distanceTo(target);
        } else {
            zoom = (this.camera as any).zoom || 1;
        }

        const data = {
            roomId: this._currentRoomId(),
            userId: this._currentUserId(),
            position: toVec3Tuple(position.x, position.y, position.z),
            target: toVec3Tuple(target.x, target.y, target.z),
            up: toVec3Tuple(this.camera.up.x, this.camera.up.y, this.camera.up.z),
            zoom,
        };

        this.socket.emit(COLLAB_EVENTS.CAMERA_UPDATE, data);
    }

    private handleCameraBroadcast(data: CollabCameraData): void {
        if (data.userId === this._currentUserId()) return;

        // Always pass camera data to external handler (for follow mode)
        // The handler checks if we're following this user
        if (this._followingUserId() === data.userId) {
            this.onCameraFollowUpdate?.(data);
        }

        // Also store camera data for UI display (same as avatar sync)
        const cameras = new Map(this._remoteUserCameras());
        cameras.set(data.userId, {
            position: data.position,
            target: data.target,
            lastUpdate: Date.now(),
        });
        this._remoteUserCameras.set(cameras);
    }

    // External callback for camera follow
    onCameraFollowUpdate: ((data: CollabCameraData) => void) | null = null;

    // ========================================================================
    // Markup Management
    // ========================================================================

    /**
     * Add a markup annotation
     */
    addMarkup(type: MarkupType, points: THREE.Vector3[], options?: {
        text?: string;
        fontSize?: number;
        lineWidth?: number;
        arrowSize?: number;
    }): string {
        const markupId = generateMarkupId();

        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) {
            return markupId;
        }

        const pointTuples = points.map(p => toVec3Tuple(p.x, p.y, p.z));

        const data = {
            roomId: this._currentRoomId(),
            markupId,
            userId: this._currentUserId(),
            userName: this.userName,
            userColor: this.userColor,
            type,
            points: pointTuples,
            ...options,
        };

        this.socket.emit(COLLAB_EVENTS.MARKUP_ADD, data);

        return markupId;
    }

    /**
     * Update a markup
     */
    updateMarkup(markupId: string, updates: {
        points?: THREE.Vector3[];
        text?: string;
    }): void {
        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) {
            return;
        }

        const data: any = {
            roomId: this._currentRoomId(),
            markupId,
            userId: this._currentUserId(),
        };

        if (updates.points) {
            data.points = updates.points.map(p => toVec3Tuple(p.x, p.y, p.z));
        }
        if (updates.text !== undefined) {
            data.text = updates.text;
        }

        this.socket.emit(COLLAB_EVENTS.MARKUP_UPDATE, data);
    }

    /**
     * Delete a markup
     */
    deleteMarkup(markupId: string): void {
        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) {
            return;
        }

        this.socket.emit(COLLAB_EVENTS.MARKUP_DELETE, {
            roomId: this._currentRoomId(),
            markupId,
            userId: this._currentUserId(),
        });
    }

    private handleMarkupSync(data: MarkupSyncData): void {
        switch (data.action) {
            case 'add':
                if (data.markup) {
                    this.markupManager?.addMarkup(data.markup);
                }
                break;
            case 'update':
                if (data.markup) {
                    this.markupManager?.updateMarkup(data.markup.markupId, data.markup);
                }
                break;
            case 'delete':
                if (data.markupId) {
                    this.markupManager?.deleteMarkup(data.markupId);
                }
                break;
        }
    }

    // ========================================================================
    // Cleanup
    // ========================================================================

    private clearAllUsers(): void {
        // Clear cursors
        for (const cursor of this.cursorManager.values()) {
            this.collabContainer?.remove(cursor);
            cursor.dispose();
        }
        this.cursorManager.clear();

        // Clear avatars
        for (const avatar of this.avatarManager.values()) {
            this.collabContainer?.remove(avatar);
            avatar.dispose();
        }
        this.avatarManager.clear();

        // Clear highlights
        this.highlightManager?.clearAll();

        // Clear timers
        for (const timer of this.cursorHideTimers.values()) {
            clearTimeout(timer);
        }
        this.cursorHideTimers.clear();
    }

    ngOnDestroy(): void {
        this.leaveRoom();

        // Disconnect socket
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }

        // Cleanup managers
        this.clearAllUsers();
        this.markupManager?.dispose();
        this.highlightManager?.dispose();

        // Remove container
        if (this.collabContainer && this.scene) {
            this.scene.remove(this.collabContainer);
        }
    }

    /**
     * Dispose the service (for manual cleanup)
     */
    dispose(): void {
        this.ngOnDestroy();
    }

    /**
     * Disconnect from the collaboration server
     */
    disconnect(): void {
        this.leaveRoom();

        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }

        this._isConnected.set(false);
        this._currentRoomId.set(null);
        this._currentUserId.set(null);
        this._connectedUsers.set([]);
        this._hasConnectedOnce = false;
    }

    /**
     * Clear all markups created by the current user
     */
    clearMyMarkups(): void {
        if (!this._currentUserId()) return;

        const myMarkups = this.markupManager?.getUserMarkups(this._currentUserId()!) || [];
        for (const markup of myMarkups) {
            this.deleteMarkup(markup.markupId);
        }
    }

    // ========================================================================
    // Scene Object Sync
    // ========================================================================

    /**
     * Emit object transform change (position, rotation, scale)
     */
    emitObjectTransform(objectName: string, position?: THREE.Vector3, rotation?: THREE.Euler, scale?: THREE.Vector3): void {
        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) return;

        const data: SceneObjectTransformData = {
            roomId: this._currentRoomId()!,
            userId: this._currentUserId()!,
            objectName,
            position: position ? toVec3Tuple(position.x, position.y, position.z) : undefined,
            rotation: rotation ? toVec3Tuple(rotation.x, rotation.y, rotation.z) : undefined,
            scale: scale ? toVec3Tuple(scale.x, scale.y, scale.z) : undefined,
        };

        console.log('[FikoCollab] Emitting object transform:', objectName);
        this.socket.emit(COLLAB_EVENTS.SCENE_OBJECT_TRANSFORM, data);
    }

    /**
     * Emit object visibility change
     */
    emitObjectVisibility(objectName: string, visible: boolean): void {
        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) return;

        const data: SceneObjectVisibilityData = {
            roomId: this._currentRoomId()!,
            userId: this._currentUserId()!,
            objectName,
            visible,
        };

        console.log('[FikoCollab] Emitting object visibility:', objectName, visible);
        this.socket.emit(COLLAB_EVENTS.SCENE_OBJECT_VISIBILITY, data);
    }

    /**
     * Emit object color change
     */
    emitObjectColor(objectName: string, color: string, opacity?: number): void {
        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) return;

        const data: SceneObjectColorData = {
            roomId: this._currentRoomId()!,
            userId: this._currentUserId()!,
            objectName,
            color,
            opacity,
        };

        console.log('[FikoCollab] Emitting object color:', objectName, color);
        this.socket.emit(COLLAB_EVENTS.SCENE_OBJECT_COLOR, data);
    }

    /**
     * Emit object delete
     */
    emitObjectDelete(objectName: string): void {
        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) return;

        const data: SceneObjectDeleteData = {
            roomId: this._currentRoomId()!,
            userId: this._currentUserId()!,
            objectName,
        };

        console.log('[FikoCollab] Emitting object delete:', objectName);
        this.socket.emit(COLLAB_EVENTS.SCENE_OBJECT_DELETE, data);
    }

    // External callbacks for scene sync (set by fiko-three-js.component)
    onSceneObjectTransform: ((data: SceneObjectTransformData) => void) | null = null;
    onSceneObjectVisibility: ((data: SceneObjectVisibilityData) => void) | null = null;
    onSceneObjectColor: ((data: SceneObjectColorData) => void) | null = null;
    onSceneObjectDelete: ((data: SceneObjectDeleteData) => void) | null = null;
    onFileShareReady: ((data: FileShareReadyData) => void) | null = null;

    /**
     * Setup scene sync socket listeners (called during initialize)
     */
    setupSceneSyncListeners(): void {
        if (!this.socket) return;

        this.socket.on(COLLAB_EVENTS.SCENE_OBJECT_SYNC, (data: SceneObjectSyncData) => {
            if (data.userId === this._currentUserId()) return;
            console.log('[FikoCollab] Scene object sync received:', data.action, data.objectName);

            switch (data.action) {
                case 'transform':
                    if (data.transform) this.onSceneObjectTransform?.(data.transform);
                    break;
                case 'visibility':
                    if (data.visibility) this.onSceneObjectVisibility?.(data.visibility);
                    break;
                case 'color':
                    if (data.color) this.onSceneObjectColor?.(data.color);
                    break;
                case 'delete':
                    this.onSceneObjectDelete?.({ roomId: '', userId: data.userId, objectName: data.objectName });
                    break;
            }
        });

        // Direct event listeners (if server sends directly instead of sync wrapper)
        this.socket.on(COLLAB_EVENTS.SCENE_OBJECT_TRANSFORM, (data: SceneObjectTransformData) => {
            if (data.userId === this._currentUserId()) return;
            this.onSceneObjectTransform?.(data);
        });

        this.socket.on(COLLAB_EVENTS.SCENE_OBJECT_VISIBILITY, (data: SceneObjectVisibilityData) => {
            if (data.userId === this._currentUserId()) return;
            this.onSceneObjectVisibility?.(data);
        });

        this.socket.on(COLLAB_EVENTS.SCENE_OBJECT_COLOR, (data: SceneObjectColorData) => {
            if (data.userId === this._currentUserId()) return;
            this.onSceneObjectColor?.(data);
        });

        this.socket.on(COLLAB_EVENTS.SCENE_OBJECT_DELETE, (data: SceneObjectDeleteData) => {
            if (data.userId === this._currentUserId()) return;
            this.onSceneObjectDelete?.(data);
        });

        // File share
        this.socket.on(COLLAB_EVENTS.FILE_SHARE_READY, (data: FileShareReadyData) => {
            if (data.userId === this._currentUserId()) return;
            console.log('[FikoCollab] File share ready:', data.fileName, data.fileUrl);
            this.onFileShareReady?.(data);
        });
    }

    // ========================================================================
    // File Sharing
    // ========================================================================

    /**
     * Notify room that a file has been uploaded and is ready for download
     */
    emitFileShareReady(fileName: string, fileUrl: string, fileSize: number, fileType: string): void {
        if (!this.socket || !this._currentRoomId() || !this._currentUserId()) return;

        const data: FileShareReadyData = {
            roomId: this._currentRoomId()!,
            userId: this._currentUserId()!,
            userName: this.userName,
            fileName,
            fileUrl,
            fileSize,
            fileType,
        };

        console.log('[FikoCollab] Emitting file share ready:', fileName);
        this.socket.emit(COLLAB_EVENTS.FILE_SHARE_READY, data);
    }
}
